import type { PropsWithChildren } from "react";
import { atom, useAtomValue } from "jotai";
import { useHydrateAtoms } from "jotai/utils";
import { useForm } from "react-hook-form";
import { useLens } from "@hookform/lenses";
import type { CompositionsForm, CompositionForm } from "@design-lab/shared/types";

type CompositionFormContextValue = {
  form: ReturnType<typeof useForm<CompositionsForm>>;
  lens: ReturnType<typeof useLens<CompositionsForm>>;
  validateComposition: (composition: CompositionForm) => true | string;
  registerValidationForAllCompositions: () => void;
};

const compositionFormAtom = atom<CompositionFormContextValue | null>(null);

function useCompositionFormCreator(): CompositionFormContextValue {
  const form = useForm<CompositionsForm>({
    defaultValues: {
      currentCompositionId: "",
      addedCompositions: []
    },
    mode: "onChange"
  });

  const lens = useLens(form);

  const validateComposition = (composition: CompositionForm) => {
    if (!composition) {
      return true;
    }
    const hasAnyVariant = Object.values(composition.variants || {}).some(quantity => quantity > 0);
    return hasAnyVariant || "Pick a size and quantity before adding to cart";
  };

  const registerValidationForAllCompositions = () => {
    const compositions = form.getValues("addedCompositions");
    compositions.forEach((_, index) => {
      form.register(`addedCompositions.${index}`, {
        validate: { hasVariants: validateComposition }
      });
    });
  };

  return {
    form,
    lens,
    validateComposition,
    registerValidationForAllCompositions
  };
}

export function CompositionFormProvider({ children }: PropsWithChildren) {
  const value = useCompositionFormCreator();
  useHydrateAtoms([[compositionFormAtom, value]], { dangerouslyForceHydrate: true });
  return children;
}

export function useCompositionForm() {
  const value = useAtomValue(compositionFormAtom);
  if (!value) {
    throw new Error("CompositionFormProvider is missing");
  }
  return value;
}
